/**
 * Background renderer \u2014 vizz.fm-style backdrop styles.
 *
 * Operates entirely on the existing `#backdrop` DOM element (positioned
 * behind the WebGL canvas) so we don't have to touch the Three.js scene.
 * Five styles:
 *   - solid:    flat color fill (audio brightens slightly on bass).
 *   - linear:   top-to-bottom CSS gradient between primary + secondary.
 *   - radial:   centered radial gradient pulsing alpha with bass.
 *   - starfield: 2D canvas with twinkling stars; treble drives twinkle.
 *   - plasma:   animated WebGL noise texture (one-shot shader, very cheap).
 *
 * Public surface kept tiny: `setStyle`, `setColors`, `setReactivity`, and a
 * `tick(features)` called once per frame from the main loop.
 */

import { AudioFeatures } from "../audio/types";

export type BgStyle = "tint" | "solid" | "linear" | "radial" | "starfield" | "plasma";

export interface BgConfig {
  style: BgStyle;
  primary: string;     // CSS color
  secondary: string;   // CSS color (used by linear/radial/plasma)
  reactivity: number;  // 0..3 \u2014 how much audio drives motion/density
  /** Linear gradient angle in degrees (0..360). */
  gradientAngle?: number;
  /** Radial gradient center as 0..1 fraction of width/height. */
  centerX?: number;
  centerY?: number;
  /** Radial gradient inner-stop sharpness (0..1). */
  sharpness?: number;
  /** Starfield density override 0..2 (default 1). */
  starDensity?: number;
  /** Starfield twinkle speed 0..3 (default 1). */
  twinkleSpeed?: number;
  /** Plasma scale 0.5..6 (default 1). */
  plasmaScale?: number;
  /** Plasma speed 0..3 (default 1). */
  plasmaSpeed?: number;
  /** Plasma complexity 1..6 (number of summed sines). */
  plasmaComplexity?: number;
}

export const DEFAULT_BG: BgConfig = {
  style: "tint",       // Original PulseFX behaviour (renderer-driven tint).
  primary: "#06122a",
  secondary: "#000000",
  reactivity: 1,
};

interface Star {
  x: number;
  y: number;
  size: number;
  twinklePhase: number;
  twinkleSpeed: number;
  baseBrightness: number;
}

export class BackgroundRenderer {
  private readonly el: HTMLElement;
  private cfg: BgConfig = { ...DEFAULT_BG };

  // Starfield canvas (lazy-created when style switches to starfield).
  private starCanvas: HTMLCanvasElement | null = null;
  private starCtx: CanvasRenderingContext2D | null = null;
  private stars: Star[] = [];
  private starResizeObserver: ResizeObserver | null = null;
  private starsLastTime = performance.now();

  // Plasma canvas (also lazy).
  private plasmaCanvas: HTMLCanvasElement | null = null;
  private plasmaCtx: CanvasRenderingContext2D | null = null;
  private plasmaTime = 0;

  // Cached, so DOM writes are skipped when nothing changes.
  private lastSolidColor = "";
  private lastGradient = "";

  constructor(el: HTMLElement) {
    this.el = el;
  }

  setConfig(cfg: Partial<BgConfig>): void {
    const styleChanged = cfg.style !== undefined && cfg.style !== this.cfg.style;
    Object.assign(this.cfg, cfg);
    if (styleChanged) {
      this.teardownCurrentStyle();
      this.applyStyleScaffold();
    }
  }

  getConfig(): BgConfig {
    return { ...this.cfg };
  }

  /**
   * Called once per frame with the latest tint computed by the renderer
   * (fallback `tintRgba`) and audio features. The `tint` arg lets the
   * renderer-driven mode keep working without code changes upstream.
   */
  tick(
    features: AudioFeatures,
    tint: { r: number; g: number; b: number; alpha: number; blurPx: number },
  ): void {
    switch (this.cfg.style) {
      case "tint":
        this.renderTint(tint);
        break;
      case "solid":
        this.renderSolid(features);
        break;
      case "linear":
        this.renderLinear(features);
        break;
      case "radial":
        this.renderRadial(features);
        break;
      case "starfield":
        this.renderStarfield(features);
        break;
      case "plasma":
        this.renderPlasma(features);
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Style: tint (legacy default \u2014 reads from renderer.backdrop)
  // -------------------------------------------------------------------------
  private renderTint(bd: { r: number; g: number; b: number; alpha: number; blurPx: number }): void {
    this.el.style.background = `radial-gradient(ellipse at 50% 45%, rgba(${bd.r}, ${bd.g}, ${bd.b}, ${bd.alpha.toFixed(3)}) 0%, #010104 70%, #000 100%)`;
    this.el.style.opacity = String(Math.min(1, bd.alpha + 0.25));
    this.el.style.filter = `blur(${bd.blurPx}px)`;
  }

  // -------------------------------------------------------------------------
  // Style: solid color (with optional bass-driven brightening)
  // -------------------------------------------------------------------------
  private renderSolid(f: AudioFeatures): void {
    const k = 1 + f.bass * 0.18 * this.cfg.reactivity;
    const c = mixColor(this.cfg.primary, "#ffffff", Math.min(0.18, (k - 1) * 0.6));
    if (c !== this.lastSolidColor) {
      this.el.style.background = c;
      this.lastSolidColor = c;
    }
    this.el.style.opacity = "1";
    this.el.style.filter = "none";
  }

  // -------------------------------------------------------------------------
  // Style: linear gradient (top \u2192 bottom)
  // -------------------------------------------------------------------------
  private renderLinear(f: AudioFeatures): void {
    const energy = Math.min(1, f.level + f.bass * 0.5);
    const baseAngle = this.cfg.gradientAngle ?? 180;
    const angle = baseAngle + Math.sin(performance.now() * 0.0003) * 8 * this.cfg.reactivity;
    const stop = 50 + energy * 18 * this.cfg.reactivity;
    const grad = `linear-gradient(${angle.toFixed(1)}deg, ${this.cfg.primary} 0%, ${this.cfg.secondary} ${stop.toFixed(0)}%)`;
    if (grad !== this.lastGradient) {
      this.el.style.background = grad;
      this.lastGradient = grad;
    }
    this.el.style.opacity = "1";
    this.el.style.filter = "none";
  }

  // -------------------------------------------------------------------------
  // Style: radial gradient pulsing with bass
  // -------------------------------------------------------------------------
  private renderRadial(f: AudioFeatures): void {
    const energy = Math.min(1, f.bass + f.level * 0.5);
    const radius = 35 + energy * 35 * this.cfg.reactivity;
    const cx = ((this.cfg.centerX ?? 0.5) * 100).toFixed(0);
    const cy = ((this.cfg.centerY ?? 0.5) * 100).toFixed(0);
    const sharp = this.cfg.sharpness ?? 0;
    const inner = (radius * (1 - sharp * 0.7)).toFixed(0);
    const grad = `radial-gradient(ellipse at ${cx}% ${cy}%, ${this.cfg.primary} 0%, ${this.cfg.primary} ${inner}%, ${this.cfg.secondary} ${radius.toFixed(0)}%, ${this.cfg.secondary} 100%)`;
    if (grad !== this.lastGradient) {
      this.el.style.background = grad;
      this.lastGradient = grad;
    }
    this.el.style.opacity = "1";
    this.el.style.filter = "none";
  }

  // -------------------------------------------------------------------------
  // Style: starfield \u2014 lazily allocated 2D canvas filling #backdrop.
  // Treble shimmer drives twinkle speed; beat puts a flash on every star.
  // -------------------------------------------------------------------------
  private ensureStarCanvas(): void {
    if (this.starCanvas) return;
    const c = document.createElement("canvas");
    c.style.position = "absolute";
    c.style.inset = "0";
    c.style.width = "100%";
    c.style.height = "100%";
    c.style.display = "block";
    this.el.appendChild(c);
    this.starCanvas = c;
    this.starCtx = c.getContext("2d");
    const resize = (): void => {
      const rect = this.el.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      c.width = Math.max(1, Math.floor(rect.width * dpr));
      c.height = Math.max(1, Math.floor(rect.height * dpr));
      this.seedStars(c.width, c.height);
    };
    resize();
    if (typeof ResizeObserver !== "undefined") {
      this.starResizeObserver = new ResizeObserver(() => resize());
      this.starResizeObserver.observe(this.el);
    }
  }

  private seedStars(w: number, h: number): void {
    const densityMult = this.cfg.starDensity ?? 1;
    const density = 0.00018 * (1 + this.cfg.reactivity * 0.4) * densityMult;
    const target = Math.min(2400, Math.max(60, Math.floor(w * h * density)));
    this.stars = [];
    for (let i = 0; i < target; i++) {
      this.stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 0.5 + Math.random() * 1.8,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: (0.4 + Math.random() * 1.6) * (this.cfg.twinkleSpeed ?? 1),
        baseBrightness: 0.4 + Math.random() * 0.6,
      });
    }
  }

  private renderStarfield(f: AudioFeatures): void {
    this.ensureStarCanvas();
    const ctx = this.starCtx;
    const c = this.starCanvas;
    if (!ctx || !c) return;
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.starsLastTime) / 1000);
    this.starsLastTime = now;
    // Background fill (primary), drawn opaque so stars wipe last frame.
    ctx.fillStyle = this.cfg.primary;
    ctx.fillRect(0, 0, c.width, c.height);
    const trebleBoost = 1 + f.treble * 1.2 * this.cfg.reactivity;
    const beatFlash = Math.min(1, f.beat * 1.4 * this.cfg.reactivity);
    ctx.fillStyle = this.cfg.secondary;
    for (const s of this.stars) {
      s.twinklePhase += dt * s.twinkleSpeed * trebleBoost;
      const twinkle = 0.55 + 0.45 * Math.sin(s.twinklePhase);
      const alpha = Math.min(1, s.baseBrightness * twinkle + beatFlash * 0.4);
      ctx.globalAlpha = alpha;
      const r = s.size * (1 + beatFlash * 0.6);
      // Cheap point: a small filled rect is faster than circles for hundreds
      // of stars and reads identically at this size.
      ctx.fillRect(s.x - r * 0.5, s.y - r * 0.5, r, r);
    }
    ctx.globalAlpha = 1;
    this.el.style.opacity = "1";
    this.el.style.filter = "none";
  }

  // -------------------------------------------------------------------------
  // Style: plasma \u2014 animated 2D noise gradient via 2D canvas. We use a
  // low-resolution offscreen draw and CSS-scale it up so we can afford a
  // pretty per-pixel-ish look for ~zero GPU cost.
  // -------------------------------------------------------------------------
  private ensurePlasmaCanvas(): void {
    if (this.plasmaCanvas) return;
    const c = document.createElement("canvas");
    c.style.position = "absolute";
    c.style.inset = "0";
    c.style.width = "100%";
    c.style.height = "100%";
    c.style.display = "block";
    c.style.imageRendering = "pixelated";
    // Low-res for cost; CSS upscales smoothly enough for a plasma look.
    c.width = 96;
    c.height = 54;
    this.el.appendChild(c);
    this.plasmaCanvas = c;
    this.plasmaCtx = c.getContext("2d");
  }

  private renderPlasma(f: AudioFeatures): void {
    this.ensurePlasmaCanvas();
    const ctx = this.plasmaCtx;
    const c = this.plasmaCanvas;
    if (!ctx || !c) return;
    const w = c.width, h = c.height;
    const speed = this.cfg.plasmaSpeed ?? 1;
    const scale = this.cfg.plasmaScale ?? 1;
    const complexity = Math.max(1, Math.min(6, Math.round(this.cfg.plasmaComplexity ?? 4)));
    this.plasmaTime += 0.016 * speed * (1 + f.level * 0.8 + f.bass * 0.6) * this.cfg.reactivity;
    const t = this.plasmaTime;
    const img = ctx.createImageData(w, h);
    const data = img.data;
    const a = parseHex(this.cfg.primary);
    const b = parseHex(this.cfg.secondary);
    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let v = 0;
        const sx = x * 0.18 * scale, sy = y * 0.22 * scale;
        if (complexity >= 1) v += Math.sin(sx + t);
        if (complexity >= 2) v += Math.sin(sy + t * 1.13);
        if (complexity >= 3) v += Math.sin((sx + sy) + t * 0.75);
        if (complexity >= 4) v += Math.sin(Math.sqrt((x - w / 2) * (x - w / 2) + (y - h / 2) * (y - h / 2)) * 0.12 * scale + t);
        if (complexity >= 5) v += Math.sin(sx * 0.5 - sy * 0.7 + t * 1.31);
        if (complexity >= 6) v += Math.sin((sx - sy) * 0.4 + t * 0.42);
        v /= complexity;
        const m = (v + 1) * 0.5;
        data[i++] = Math.round(a.r + (b.r - a.r) * m);
        data[i++] = Math.round(a.g + (b.g - a.g) * m);
        data[i++] = Math.round(a.b + (b.b - a.b) * m);
        data[i++] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    this.el.style.opacity = "1";
    this.el.style.filter = "none";
  }

  // -------------------------------------------------------------------------
  // Style switching helpers
  // -------------------------------------------------------------------------
  private teardownCurrentStyle(): void {
    if (this.starCanvas) {
      this.starCanvas.remove();
      this.starCanvas = null;
      this.starCtx = null;
      this.stars = [];
    }
    if (this.starResizeObserver) {
      this.starResizeObserver.disconnect();
      this.starResizeObserver = null;
    }
    if (this.plasmaCanvas) {
      this.plasmaCanvas.remove();
      this.plasmaCanvas = null;
      this.plasmaCtx = null;
    }
    this.el.style.background = "";
    this.lastSolidColor = "";
    this.lastGradient = "";
  }

  private applyStyleScaffold(): void {
    // Preallocate the canvas/scaffolds the new style needs so the first
    // tick doesn't flash an unstyled backdrop.
    if (this.cfg.style === "starfield") this.ensureStarCanvas();
    if (this.cfg.style === "plasma") this.ensurePlasmaCanvas();
  }
}

// ---------------------------------------------------------------------------
// Tiny color helpers (shared with the rest of the file). Kept inline so the
// background module has zero deps beyond audio types.
// ---------------------------------------------------------------------------
function parseHex(s: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(s.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const v = parseInt(m[1], 16);
  return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
}
function mixColor(a: string, b: string, t: number): string {
  const ca = parseHex(a), cb = parseHex(b);
  const r = Math.round(ca.r + (cb.r - ca.r) * t);
  const g = Math.round(ca.g + (cb.g - ca.g) * t);
  const bb = Math.round(ca.b + (cb.b - ca.b) * t);
  return `rgb(${r}, ${g}, ${bb})`;
}
